import { useCallback } from 'react'

import { create } from '@bufbuild/protobuf'
import { createQueryOptions, useTransport } from '@connectrpc/connect-query'
import { BusinessProcess } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type { ApprovalTask } from '@ebalance/proto/bffuser/v1/approval_task_model_pb'
import { getBidLimitMatrix } from '@ebalance/proto/bffuser/v1/bid_limit_service-BidLimitService_connectquery'
import { getBiddingSpotScreen } from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import {
  BiddingSpotScreenSourceStatus,
  GetBiddingSpotScreenRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { buildBidLimits } from '~/models/bidLimits'
import { buildBlockApprovalChecks } from '~/pages/SpotBidding/models/blockApprovalViolation'
import { convertSpotBidsFromApprovalRequest } from '~/pages/SpotBidding/models/convertSpotBidsFromApprovalRequest'
import {
  buildSpotApprovalChecks,
  selectRequestBids,
  selectRevalidationBids,
  selectRevalidationBlockVolumes,
} from '~/pages/SpotBidding/models/spotApprovalDecision'

/**
 * スポット / ブロック入札の承認が入札制限違反で断られた時に、 入札画面の承認確認モーダルと同じ違反
 * サマリ (違反件数・違反行の詳細: 対象エリア、 設定上限/下限、 実際の入札値。 AC-BSP-097) を組む。
 *
 * 断った理由 (ErrorInfo) には違反の中身が載らないので、 入札画面と同じ材料 (受渡日の画面データ =
 * 通常入札 + ブロック入札の札、 入札制限) を取り直し、 入札画面と同じ組み立て
 * (`buildSpotApprovalChecks` / `buildBlockApprovalChecks`) に渡す。 コマ計の超過 (AC-BSP-080) も
 * 同じサマリに入る。 材料を取れない・読み込みが不完全・違反が組めない時は undefined を返し、
 * 呼び出し元が理由だけを伝える文に落とす (違反なしと見せない)。
 */
export function useSpotBlockViolationSections(): (
  task: ApprovalTask,
) => Promise<ViolationSection[] | undefined> {
  const { t } = useTranslation()
  const transport = useTransport()
  const queryClient = useQueryClient()

  return useCallback(
    async (task) => {
      if (task.targetDate === undefined) return undefined
      try {
        // 承認を断られた直後の判断材料なので、 どちらも古い値は使わない。
        const [screen, matrix] = await Promise.all([
          queryClient.fetchQuery({
            ...createQueryOptions(
              getBiddingSpotScreen,
              create(GetBiddingSpotScreenRequestSchema, {
                targetDate: task.targetDate,
              }),
              { transport },
            ),
            staleTime: 0,
          }),
          queryClient.fetchQuery({
            ...createQueryOptions(
              getBidLimitMatrix,
              { bidSegment: BidSegment.SPOT_NORMAL },
              { transport },
            ),
            staleTime: 0,
          }),
        ])
        // 一部のデータ源が欠けた画面データでは母集合が足りず、 「違反なし」 と見せかねない。
        if (
          screen.spotStatus !== BiddingSpotScreenSourceStatus.OK ||
          screen.blockStatus !== BiddingSpotScreenSourceStatus.OK
        ) {
          return undefined
        }
        const limits = buildBidLimits(matrix.cells, BidSegment.SPOT_NORMAL)
        const bids = convertSpotBidsFromApprovalRequest(
          screen.spotApprovalRequests,
        )
        const revalidationBids = selectRevalidationBids(bids)
        // 上限数量(コマ計) の母集合は入札画面と同じ (通常入札の有効札 + 有効なブロック入札)。
        const volumeTotalRows = [
          ...revalidationBids,
          ...selectRevalidationBlockVolumes(screen.blockApprovalRequests),
        ]
        const blockGroupSections = (id: string): ViolationSection[] => {
          const group = screen.blockApprovalRequests.find((g) => g.id === id)
          return group === undefined
            ? []
            : buildBlockApprovalChecks(
                group,
                {
                  limits,
                  volumeTotalRows,
                  isLimitsUnavailable: false,
                  isSpotVolumeUnresolved: false,
                },
                t,
              ).violationSections
        }
        const sections: ViolationSection[] =
          task.businessProcess === BusinessProcess.BLOCK_BID
            ? blockGroupSections(task.id)
            : buildSpotApprovalChecks(
                selectRequestBids(bids, task.id),
                {
                  revalidationBids,
                  volumeTotalRows,
                  limits,
                  isLimitsUnavailable: false,
                  isBlockVolumeUnresolved: false,
                },
                t,
              ).violationSections
        const withDetails = sections.filter((s) => s.details.length > 0)
        return withDetails.length === 0 ? undefined : withDetails
      } catch {
        return undefined
      }
    },
    [queryClient, t, transport],
  )
}
